import React, { useState } from 'react';
import { IListingData } from '@models/listing';
import StaticLayout from '@components/layout/StaticLayout';
import { useLazyQuery } from '@apollo/client';
import { filterQuery, listQuery } from './queries';
import { dataToLovs, mapToListingData } from './utils';
import { filterSectionsContent, headers } from './content';
import { IListingFilterWidgetSection } from '@components/widgets/listing-filter-widget';
import ListingFilterWidget from '@components/widgets/listing-filter-widget/ListingFilterWidget';
import { IAbstractRecord } from '@models';
import _ from 'lodash';
import { getFilter, setFilter } from '@utils/filter-utils';
import ToastErrorMessage from '@components/toast/ToastErrorMessage';
import { toast } from 'react-toastify';
import { DEFAULT_ERROR_TEXT } from '@utils';
import { capitalizeFirstCharacter } from '@utils/formatting-utils';
import { useAppSelector } from '@redux/hooks';
import ClausePopUpShell from '../clause-popup/ClausePopUpShell';
import { IEnhanceTableHeaderClickable } from '@components/table/widgets/shared-with-old';
import DateService from '@services/DateService';
import { IEnhancedMenuItem } from '@components/table/types';
import { IFilterModel } from '@components/widgets/listing-widget';
import ListingWidget from '@components/widgets/listing-widget/ListingWidget';

interface IClauseLineData {
  lineId: string;
  lineName: string;
  clauseId: string;
}

const PAGE_CONTEXT = 'Clauses';
const FILTER_SESSION_KEY = 'clausesFilter';

const ClausesListingPage: React.FC = () => {
  const user = useAppSelector((state) => state.user);

  const [loading, setLoading] = useState<boolean>(false);
  const [clauseDrawerStatus, setClauseDrawerStatus] =
    useState<IClauseLineData>();
  const [filterSections, setFilterSections] =
    useState<IListingFilterWidgetSection[]>();

  const initialfilterValues =
    Object.keys(getFilter(FILTER_SESSION_KEY)).length > 0
      ? getFilter(FILTER_SESSION_KEY)
      : {
          line: [],
          SelectedStatuses: [],
          createdOn: [],
        };

  const [filterValues, setFilterValues] = useState<IFilterModel>({
    namedFilters: initialfilterValues,
    pagination: {
      pageSize: 10,
      pageNumber: 1,
    },
    descending: true,
  });

  const [tableData, setTableData] = useState<IListingData>({
    pagedItems: {},
    pageSize: 10,
    pageNumber: 1,
    totalCount: 0,
  });

  const isAdmin = user.userRoles.includes('Insurance-Admin');
  const actions: IEnhancedMenuItem[] = isAdmin
    ? [
        {
          name: 'new',
          title: '+ New',
          onClick: () => {
            setClauseDrawerStatus({
              lineId: '',
              clauseId: '',
              lineName: '',
            });
          },
        },
      ]
    : [];

  const [getClausesLazy] = useLazyQuery(listQuery());
  const [filterResponse] = useLazyQuery(filterQuery());

  const getListingData = (filter = filterValues) => {
    if (!filter) {
      setLoading(false);
      return {};
    }

    setLoading(true);
    setFilter(filter, FILTER_SESSION_KEY);
    setFilterValues(filter);

    const { namedFilters, pagination } = filter;

    const formattedDateRange = DateService.formatDateRangeBackend(
      namedFilters.createdOn
    );
    return getClausesLazy({
      variables: {
        keywordSearch: filter.searchKey,
        currentPage: pagination.pageNumber || 1,
        currentPageSize: pagination.pageSize || 10,
        Attribute:
          capitalizeFirstCharacter(filter.orderBy || '') ||
          'PlanConfigManagement_Clause_Name',
        Descending: filter.descending,
        SelectedLineId: namedFilters.line || [],
        SelectedStatuses: namedFilters.status || null,
        FromCreatedOn: formattedDateRange?.[0],
        ToCreatedOn: formattedDateRange?.[1],
      },
    })
      .then(({ data }) => {
        if (data) {
          setTableData(mapToListingData(data));
          filterResponse().then(({ data: filterData }) => {
            setFilterSections(
              filterSectionsContent(dataToLovs(filterData), namedFilters)
            );
          });
        }
      })
      .catch(() => {
        toast.error(
          <ToastErrorMessage>{DEFAULT_ERROR_TEXT}</ToastErrorMessage>
        );
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const handlePageChange = (
    page: number,
    filterModel = filterValues
  ): Promise<void> => {
    return new Promise<void>(() =>
      getListingData({
        ...filterModel,
        pagination: {
          ...filterModel.pagination,
          pageNumber: page,
        },
      })
    );
  };

  (
    headers.planConfigManagement_Clause_Name as IEnhanceTableHeaderClickable
  ).callback = (payload: IAbstractRecord) => {
    setClauseDrawerStatus({
      lineId: payload.columns.lineId,
      clauseId: payload.columns.planConfigManagement_Clause_Id,
      lineName: payload.columns.planConfigManagement_Clause_LineID,
    });
  };

  const renderMainChildren = () => {
    return (
      <>
        <ListingWidget
          title="Clauses"
          name="clauses"
          data={tableData}
          tableSettings={{ headers }}
          orderByAscendingByDefault
          defaultOrderByColumn="planConfigManagement_Clause_Name"
          loading={loading}
          pageContext={PAGE_CONTEXT}
          onPageChange={(filterModel) => {
            const newFilterModel = {
              ...filterValues,
              ...filterModel,
            };
            const page = filterModel.pagination.pageNumber;
            return handlePageChange(page, newFilterModel);
          }}
          actions={actions}
          usePagination
          disableSelection
        />
        {!!clauseDrawerStatus && (
          <ClausePopUpShell
            open={!!clauseDrawerStatus}
            onClose={() => setClauseDrawerStatus(undefined)}
            onSuccess={() => {
              handlePageChange(0);
            }}
            lineId={clauseDrawerStatus.lineId || null}
            clauseId={clauseDrawerStatus.clauseId || null}
            isLineFieldEnabled={true}
          />
        )}
      </>
    );
  };

  const onFilterUpdate = (newFilters: IAbstractRecord) => {
    const updatedFilters = {
      ...filterValues,
      namedFilters: newFilters,
    };
    if (!_.isEqual(updatedFilters, filterValues)) {
      getListingData(updatedFilters);
    }
  };

  const renderFilter = () => {
    return (
      filterSections && (
        <ListingFilterWidget
          name={''}
          filters={filterSections}
          onApplyFilter={(v) => {
            onFilterUpdate(v);
          }}
        />
      )
    );
  };

  return (
    <StaticLayout
      name={'Applications'}
      leftChildren={renderFilter()}
      mainChildren={renderMainChildren()}
    />
  );
};

export default ClausesListingPage;
